/**
 * WhatsApp AI Booking — dispatcher controls (M4): settings, needs-attention list,
 * per-conversation AI state with Take over / Hand back, booking claims + Resolve.
 */
import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Bot, UserRound, AlertTriangle, CheckCircle2, Link2 } from "lucide-react";

export const REASON_LABELS = {
  passenger_requested: "Passenger asked for a person",
  address_or_quote_failed: "Address or fare could not be confirmed",
  booking_needs_dispatcher: "Booking needs a dispatcher",
  engine_error: "AI error",
  stale: "Message waited too long",
  max_attempts: "AI failed repeatedly",
  turn_cap: "Conversation too long",
  dispatcher_reply: "Dispatcher replied",
  dispatcher_manual: "Dispatcher took over",
  rate_limited: "Too many messages (rate limit)",
};

const CLAIM_LABELS = {
  creating: "Creating…", created: "Booked", needs_dispatcher: "Needs dispatcher",
  resolved_by_dispatcher: "Resolved by dispatcher",
};

const useConfirm = () => {
  const [state, setState] = useState(null);
  const ask = (title, description, onYes) => setState({ title, description, onYes });
  const dialog = (
    <AlertDialog open={!!state} onOpenChange={(o) => !o && setState(null)}>
      <AlertDialogContent data-testid="wa-ai-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{state?.title}</AlertDialogTitle>
          <AlertDialogDescription>{state?.description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="wa-ai-confirm-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction data-testid="wa-ai-confirm-ok" onClick={() => { state?.onYes(); setState(null); }}>Confirm</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
  return [ask, dialog];
};

const AccessModeRow = ({ mode, limit, ask, save }) => (
  <div className="space-y-1" data-testid="wa-ai-access-mode">
    <div className="text-xs font-bold">Passenger access</div>
    <div className="flex gap-1">
      {["pilot", "public"].map((m) => (
        <Button key={m} size="sm" variant={mode === m ? "default" : "outline"} data-testid={`wa-ai-mode-${m}`}
          onClick={() => mode !== m && ask(m === "public" ? "Open WhatsApp AI to all passengers?" : "Limit WhatsApp AI to pilot numbers?",
            m === "public" ? "Every valid WhatsApp number gets AI replies (when the toggle and master setting are on). Rate limit and handover still apply."
              : "Only numbers on the pilot list will get AI replies.",
            () => save({ access_mode: m }, m === "public" ? "Public mode on" : "Pilot mode on"))}>
          {m === "public" ? "Public (all passengers)" : "Pilot (listed numbers)"}
        </Button>
      ))}
    </div>
    <div className="text-[11px] text-muted-foreground" data-testid="wa-ai-rate-limit">
      Rate limit: {limit ? `${limit} AI messages per number per hour, then handed to a dispatcher` : "none"}
    </div>
  </div>
);

export function AiSettingsCard() {
  const [s, setS] = useState(null);
  const [list, setList] = useState("");
  const [ask, dialog] = useConfirm();
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/whatsapp/ai-booking/settings");
      setS(data); setList((data.allowlist || []).join("\n"));
    } catch (e) { /* non-admin or offline */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  const save = async (body, msg) => {
    try { await api.put("/admin/whatsapp/ai-booking/settings", body); toast.success(msg); load(); }
    catch (e) { toast.error(formatApiError(e) || "Could not save"); }
  };
  if (!s) return null;
  return (
    <div className="bg-white border border-border p-4 space-y-3" data-testid="wa-ai-settings-card">
      <div className="flex items-center justify-between gap-2">
        <div className="font-bold text-sm flex items-center gap-1.5"><Bot className="w-4 h-4 text-[#166534]" /> WhatsApp AI booking</div>
        <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 ${s.effective ? "bg-[#DCFCE7] text-[#166534]" : "bg-secondary text-muted-foreground"}`} data-testid="wa-ai-effective-badge">
          {s.effective ? "Active" : "Off"}
        </span>
      </div>
      <div className="text-xs text-muted-foreground" data-testid="wa-ai-env-state">
        Master setting: <strong>{s.env_enabled ? "on" : "off"}</strong> (server configuration, read-only)
      </div>
      <label className="flex items-center justify-between text-sm">
        Admin toggle
        <Switch checked={!!s.enabled} data-testid="wa-ai-toggle"
          onCheckedChange={(v) => ask(v ? "Turn WhatsApp AI on?" : "Turn WhatsApp AI off?",
            v ? "AI replies to pilot numbers (Pilot mode) or every passenger (Public mode), only when the master setting is on." : "The AI stops replying immediately. Messages still reach this Inbox.",
            () => save({ enabled: v }, v ? "WhatsApp AI toggle on" : "WhatsApp AI toggle off"))} />
      </label>
      <AccessModeRow mode={s.access_mode} limit={s.rate_limit_per_hour} ask={ask} save={save} />
      <div className="space-y-1">
        <div className="text-xs font-bold">Pilot numbers (one per line)</div>
        <Textarea rows={3} value={list} onChange={(e) => setList(e.target.value)} data-testid="wa-ai-allowlist-input" placeholder="+447700900123" />
        <Button size="sm" variant="outline" data-testid="wa-ai-allowlist-save"
          onClick={() => ask("Update pilot numbers?", "Only these numbers will get AI replies.",
            () => save({ allowlist: list.split(/[\n,]+/).map((x) => x.trim()).filter(Boolean) }, "Pilot numbers saved"))}>
          Save pilot numbers
        </Button>
      </div>
      {dialog}
    </div>
  );
}

export function AttentionList({ onOpen, onLoaded }) {
  const [data, setData] = useState({ conversations: [], claims: [], count: 0 });
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data: d } = await api.get("/admin/whatsapp/ai-booking/attention");
        if (!alive) return;
        setData(d);
        onLoaded?.(new Set([...d.conversations.map((c) => c.wa_id), ...d.claims.map((c) => c.wa_id)]));
      } catch (e) { /* ignore */ }
    };
    load();
    const t = setInterval(load, 4000);
    return () => { alive = false; clearInterval(t); };
  }, [onLoaded]);
  return (
    <div className="bg-white border border-border p-4 lg:col-span-2" data-testid="wa-ai-attention-list">
      <div className="font-bold text-sm flex items-center gap-1.5 mb-2">
        <AlertTriangle className="w-4 h-4 text-[#B45309]" /> Needs attention
        <span className="bg-[#B45309] text-white text-[10px] font-bold rounded-full px-1.5" data-testid="wa-ai-attention-count">{data.count}</span>
      </div>
      {data.count === 0 ? (
        <div className="text-xs text-muted-foreground italic" data-testid="wa-ai-attention-empty">Nothing needs a dispatcher right now.</div>
      ) : (
        <div className="space-y-1 max-h-40 overflow-y-auto">
          {data.claims.map((c) => (
            <button key={c.id} onClick={() => onOpen(c.wa_id)} className="w-full text-left text-xs px-2 py-1.5 bg-[#FFFBEB] hover:bg-[#FEF3C7] flex justify-between gap-2" data-testid={`wa-ai-attention-claim-${c.id}`}>
              <span className="font-bold">{c.wa_id}</span>
              <span>Booking: {CLAIM_LABELS[c.state] || c.state}{c.state === "creating" ? " (stuck)" : ""}</span>
            </button>
          ))}
          {data.conversations.map((c) => (
            <button key={c.wa_id} onClick={() => onOpen(c.wa_id)} className="w-full text-left text-xs px-2 py-1.5 bg-secondary/60 hover:bg-secondary flex justify-between gap-2" data-testid={`wa-ai-attention-conv-${c.wa_id}`}>
              <span className="font-bold">{c.wa_id}</span>
              <span>{REASON_LABELS[c.paused_reason] || c.paused_reason}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ResolveForm({ claim, onDone }) {
  const [rideId, setRideId] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/whatsapp/ai-booking/claims/${claim.id}/resolve`, { ride_id: rideId.trim() || null, note });
      toast.success("Booking marked as resolved"); onDone();
    } catch (e) { toast.error(formatApiError(e) || "Could not resolve"); onDone(); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-1.5 pt-1" data-testid={`wa-ai-resolve-form-${claim.id}`}>
      <Input value={rideId} onChange={(e) => setRideId(e.target.value)} placeholder="Existing ride ID to link (optional)" data-testid="wa-ai-resolve-ride-input" />
      <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note, e.g. booked by phone" data-testid="wa-ai-resolve-note-input" />
      <Button size="sm" disabled={busy} onClick={submit} data-testid="wa-ai-resolve-submit">Resolve</Button>
    </div>
  );
}

export function ConversationAiCard({ waId }) {
  const [st, setSt] = useState(null);
  const [resolving, setResolving] = useState(null);
  const [ask, dialog] = useConfirm();
  const load = useCallback(async () => {
    try { const { data } = await api.get(`/admin/whatsapp/conversations/${encodeURIComponent(waId)}/ai`); setSt(data); }
    catch (e) { setSt(null); }
  }, [waId]);
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);
  const act = async (action) => {
    try { await api.post(`/admin/whatsapp/conversations/${encodeURIComponent(waId)}/ai/${action}`); toast.success(action === "pause" ? "You have taken over" : "Handed back to AI"); load(); }
    catch (e) { toast.error(formatApiError(e) || "Action failed"); }
  };
  if (!st) return null;
  return (
    <div className="px-4 py-2 border-b border-border bg-secondary/30 space-y-2 text-xs" data-testid="wa-ai-conversation-card">
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 font-bold" data-testid="wa-ai-state-badge">
          {st.ai_paused ? <><UserRound className="w-3.5 h-3.5" /> Dispatcher handling</> : <><Bot className="w-3.5 h-3.5 text-[#166534]" /> AI handling</>}
          {st.ai_paused && st.paused_reason && <span className="font-normal text-muted-foreground" data-testid="wa-ai-handover-reason">· {REASON_LABELS[st.paused_reason] || st.paused_reason}</span>}
        </span>
        {st.ai_paused ? (
          <Button size="sm" variant="outline" data-testid="wa-ai-handback-btn"
            onClick={() => ask("Hand back to AI?", "The AI will answer this passenger's next message.", () => act("resume"))}>Hand back to AI</Button>
        ) : (
          <Button size="sm" variant="outline" data-testid="wa-ai-takeover-btn" onClick={() => act("pause")}>Take over</Button>
        )}
      </div>
      {(st.claims || []).map((c) => (
        <div key={c.id} className="border border-border bg-white px-2 py-1.5" data-testid={`wa-ai-claim-${c.id}`}>
          <div className="flex items-center justify-between gap-2">
            <span className="font-bold inline-flex items-center gap-1" data-testid={`wa-ai-claim-state-${c.id}`}>
              {c.state === "created" || c.state === "resolved_by_dispatcher" ? <CheckCircle2 className="w-3.5 h-3.5 text-[#166534]" /> : <AlertTriangle className="w-3.5 h-3.5 text-[#B45309]" />}
              {CLAIM_LABELS[c.state] || c.state}
            </span>
            {c.reference && <span className="font-mono" data-testid={`wa-ai-claim-ref-${c.id}`}>{c.reference}</span>}
          </div>
          {c.ride_id && (
            <button type="button" onClick={() => { navigator.clipboard?.writeText(c.ride_id); toast.success("Ride ID copied — search it in Rides"); }}
              className="inline-flex items-center gap-1 text-[#166534] underline" title={c.ride_id} data-testid={`wa-ai-claim-ride-link-${c.id}`}>
              <Link2 className="w-3 h-3" /> Ride {c.ride_id.slice(0, 8)} (copy ID)
            </button>
          )}
          {["needs_dispatcher", "creating"].includes(c.state) && (resolving === c.id
            ? <ResolveForm claim={c} onDone={() => { setResolving(null); load(); }} />
            : <Button size="sm" variant="outline" className="mt-1" data-testid={`wa-ai-resolve-btn-${c.id}`} onClick={() => setResolving(c.id)}>Resolve</Button>)}
        </div>
      ))}
      {dialog}
    </div>
  );
}
